import { ControlPoint } from "@mui/icons-material";
import { Select, MenuItem, Button } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import PropTypes from "prop-types";
import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";

import { ENTITY_TYPE_PROVIDER } from "../../../../../../../globalConstants";
import DataContext from "../../../../../../context/DataContext";
import useMedicalCompleteLock from "../../../../../../hooks/useMedicalCompleteLock";
import useRevalidateParent from "../../../../../../hooks/useRevalidateParent";
import axios from "../../../../../api/axios";
import { provider } from "../../../../../api/endpoints/provider";
import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import FormField from "../../../../../global/FormComponents/field/FormField";
import Modal from "../../../../../global/Modal";
import ResponseModal from "../../../../../global/ResponseModal";
import { MEDICALLY_COMPLETE_LOCK_MESSAGE } from "../../../../../modules/ClientDetail/Case/utils/constants";
import { selectStyles } from "../../constant";

import AppointmentForm from "./AppointmentForm";
import formatBulkAppointmentErrors from "./utils/bulkAppointmentErrors";

export default function BulkCreateAppointment({ setOpenAddAppts, caseInfo, open, onClose, title }) {
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const appointmentLocked = isAppointmentLocked(caseInfo);
  const { accessToken, loggedInUser } = useContext(DataContext);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [providerList, setProviderList] = useState([]);
  const [providerTypeList, setProviderTypeList] = useState([]);
  const [selectedProviderType, setSelectedProviderType] = useState("");
  const [loading, setLoading] = useState(false);
  const [formEntries, setFormEntries] = useState([1]);
  const inputRefs = useRef([]);
  const params = useParams();

  const isProvider =
    loggedInUser.entity && loggedInUser.entity.entity_type_label === ENTITY_TYPE_PROVIDER;

  const addAppointment = () => {
    setFormEntries((prev) => {
      const lastElm = prev.at(-1);
      return [...prev, lastElm + 1];
    });
  };

  const handleDelete = (id) => {
    const deleteIdx = formEntries.findIndex((idEntry) => idEntry === id);
    const newFormEntries = [...formEntries];
    newFormEntries.splice(deleteIdx, 1);
    setFormEntries(newFormEntries);
    inputRefs.current.splice(deleteIdx, 1);
  };

  const caseId = caseInfo?.client?.id || caseInfo?.client?.pk;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (appointmentLocked) {
      setErrorArray([MEDICALLY_COMPLETE_LOCK_MESSAGE]);
      setOpenError(true);
      return;
    }

    const hasInvalidDates = inputRefs.current.some((ref) => ref?.hasDateError?.());
    if (hasInvalidDates) {
      setErrorArray(["Please correct invalid appointment dates before saving."]);
      setOpenError(true);
      return;
    }

    const isFilled = (d) =>
      d !== null &&
      d !== undefined &&
      ((d.status !== null && d.status !== undefined) ||
        (d.scheduledDate !== null && d.scheduledDate !== undefined && d.scheduledDate !== "") ||
        (d.scheduledTime !== null && d.scheduledTime !== undefined && d.scheduledTime !== ""));

    const rows = inputRefs.current.reduce((acc, ref) => {
      if (!ref) {
        return acc;
      }
      const d = ref.getValues?.();
      if (isFilled(d)) {
        acc.push(d);
      }
      return acc;
    }, []);

    let dataSubmit = [
      {
        status: "",
        client: caseId,
        provider: selectedProvider,
        provider_type: selectedProviderType,
      },
    ];

    if (rows.length > 0) {
      dataSubmit = rows.map((d) => {
        const dateStr = d.scheduledDate || null;
        const timeStr = d.scheduledTime || (d.scheduledDate ? "00:00:00" : null);

        const payload = {
          status: d.status,
          client: caseId,
          provider: selectedProvider,
          provider_type: selectedProviderType,
        };

        if (dateStr) {
          payload.appointment_date = dateStr;
        }
        if (timeStr) {
          payload.appointment_time = timeStr;
        }

        return payload;
      });
    }

    try {
      setLoading(true);
      await axios.post("/api/appointments/", dataSubmit, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setOpenSuccess(true);
    } catch (error) {
      const res = error.response?.data || [];
      const errArr = ApiRequestErrorHandler(formatBulkAppointmentErrors(res));
      setErrorArray(errArr);
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSuccess = () => {
    setOpenAddAppts(false);
    revalidateParent(); // This also revalidate the children
  };

  const fetchProviders = useCallback(async (fieldOptionsUrls) => {
    return Promise.all(fieldOptionsUrls.map(async (url) => await url))
      .then((res) => res)
      .catch((error) => {
        const errArr = ApiRequestErrorHandler(error.response);
        setErrorArray(errArr);
        setOpenError(true);
      });
  }, []);

  useEffect(() => {
    const filterProvidersBasedOnRole = (data, isProvider) => {
      const newArr = [];
      for (let i = 0; i < data.length; i++) {
        if (isProvider) {
          if (data[i].can_edit) {
            newArr.push(data[i]);
          }
        } else {
          newArr.push(data[i]);
        }
      }
      return newArr;
    };

    const fieldOptionsUrls = [provider.getRelatedProviders(accessToken, params.id)];

    fetchProviders(fieldOptionsUrls)
      .then(async (results) => {
        if (results && results[0]) {
          setProviderList(filterProvidersBasedOnRole(results[0].results, isProvider));
        }
        return results;
      })
      .then(async (results) => results)
      .catch((error) => {
        const errArr = ApiRequestErrorHandler(error.response);
        setErrorArray(errArr);
        setOpenError(true);
      });
  }, [accessToken, fetchProviders, params.id, isProvider]);

  const handleSelectedProvider = async (e) => {
    setProviderTypeList([]);
    setSelectedProviderType("");
    setSelectedProvider(e.target.value);
  };

  useEffect(() => {
    const fetchProviderTypes = async () => {
      try {
        const response = await axios.get(`/api/provider/${selectedProvider}/`, {
        });
        const results = response.data?.provider_types;
        setProviderTypeList(results);
      } catch (err) {
        console.error(err);
      }
    };
    if (selectedProvider) {
      fetchProviderTypes();
    }
  }, [selectedProvider, accessToken]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      actions={[
        {
          label: "Create Appointments",
          onClick: caseId ? handleSubmit : () => {},
          isLoading: loading,
          disabled: appointmentLocked,
        },
      ]}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="flex w-full flex-col justify-center gap-4 bg-white">
          <FormField label="Select a Provider From This Case">
            <Select
              data-testid="provider-select"
              className="w-full"
              sx={{
                ...selectStyles,
                "& fieldset": { borderColor: "#9ca3af" },
              }}
              value={selectedProvider}
              onChange={(event) => handleSelectedProvider(event)}
            >
              {providerList.map((prov) => (
                <MenuItem value={prov.key} key={prov.key}>
                  {prov.value}
                </MenuItem>
              ))}
            </Select>
          </FormField>

          {selectedProvider > 0 && (
            <FormField label="Provider Type">
              <Select
                className="w-full"
                sx={{
                  ...selectStyles,
                  "& fieldset": { borderColor: "#9ca3af" },
                }}
                value={selectedProviderType}
                onChange={(event) => setSelectedProviderType(event.target.value)}
                placeholder="Please select a provider type"
                displayEmpty
                renderValue={(value) =>
                  !value ? (
                    <span className="text-gray-400">Please select a provider type</span>
                  ) : (
                    providerTypeList.find((p) => p.id === value)?.name
                  )
                }
              >
                {providerTypeList?.map((protypes) => (
                  <MenuItem value={protypes.id} key={protypes.id}>
                    {protypes.name}
                  </MenuItem>
                ))}
              </Select>
            </FormField>
          )}

          <FormField label="Appointments">
            <div className="flex flex-col gap-2">
              {formEntries.map((id, idx) => (
                <AppointmentForm
                  key={id}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  onDelete={() => handleDelete(id)}
                  canDeleteRow={formEntries.length > 1}
                />
              ))}

              {formEntries.length < 20 && (
                <div className="pr-[6px] text-end">
                  <Button
                    className="normal-case"
                    color="success"
                    endIcon={<ControlPoint />}
                    onClick={addAppointment}
                  >
                    <span className="text-black">Add another appointment</span>
                  </Button>
                </div>
              )}
            </div>
          </FormField>
        </div>
      </LocalizationProvider>

      <ResponseModal
        title="Appointments Created"
        description="Appointments have been added to clients case"
        openBool={openSuccess}
        setOpenBool={setOpenSuccess}
        handleCloseFunc={handleOpenSuccess}
      />
      <ResponseModal
        title="Error Creating Appointments"
        isError={true}
        openBool={openError}
        setOpenBool={setOpenError}
        errorMessage={errorArray}
      />
    </Modal>
  );
}

BulkCreateAppointment.propTypes = {
  setOpenAddAppts: PropTypes.func,
  activeProvObj: PropTypes.object,
  caseInfo: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
};
